import { useState, useEffect } from 'react';

interface NavbarProps {
  onCtaClick: (label: string) => void; // CTAクリックの関数を受け取る
}

export default function Navbar({ onCtaClick }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('#home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const sectionIds = ['#home', '#pain', '#solution', '#director', '#voice', '#access', '#contact'];
    const elements = sectionIds.map((id) => document.querySelector(id)).filter(Boolean);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          const id = visible[0].target.getAttribute('id');
          if (id) setActiveSection(`#${id}`);
        }
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const navLinks = [
    { label: 'ホーム', href: '#home' },
    { label: 'お悩み', href: '#pain' },
    { label: '当院の強み', href: '#solution' },
    { label: '院長紹介', href: '#director' },
    { label: '患者様の声', href: '#voice' },
    { label: 'アクセス', href: '#access' },
  ];

  const scrollToSection = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isActive = (href: string) => activeSection === href;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 py-3 md:py-4 flex items-center justify-between">
        <a
          href="#home"
          onClick={(e) => { e.preventDefault(); scrollToSection('#home'); }}
          className={`font-serif text-lg md:text-xl font-bold tracking-wide transition-colors ${
            scrolled ? 'text-clinic-green-dark' : 'text-white'
          }`}
        >
          ひろカイロ整体院
        </a>

        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
              className={`relative text-sm font-medium transition-colors hover:opacity-80 whitespace-nowrap pb-1 ${
                scrolled ? 'text-clinic-dark' : 'text-white'
              } ${isActive(link.href) ? 'opacity-100' : 'opacity-80'}`}
            >
              {link.label}
              <span
                className={`absolute bottom-0 left-0 h-0.5 rounded-full transition-all duration-300 ${
                  scrolled ? 'bg-clinic-terracotta' : 'bg-white'
                } ${isActive(link.href) ? 'w-full' : 'w-0'}`}
              />
            </a>
          ))}
          <a
            href="https://lin.ee/yWTagrY"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { onCtaClick('Navbar_LINE'); }}
            className="bg-clinic-line text-white text-sm font-bold px-5 py-2.5 rounded-full hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            LINE予約
          </a>
        </div>

        <button
          type="button"
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="メニュー"
        >
          <i className={`ri-menu-line text-2xl ${scrolled ? 'text-clinic-dark' : 'text-white'}`} />
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-1">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
              className={`block text-sm font-medium py-3 px-2 rounded-lg transition-colors ${
                isActive(link.href)
                  ? 'bg-clinic-green-light text-clinic-green-dark font-bold'
                  : 'text-clinic-dark'
              }`}
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => { onCtaClick('NavbarSp_LINE'); e.preventDefault(); scrollToSection('#contact'); }}
            className="block bg-clinic-line text-white text-sm font-bold px-5 py-3 rounded-full text-center whitespace-nowrap mt-2"
          >
            LINEで予約・相談
          </a>
        </div>
      )}
    </nav>
  );
}